import React, { useState } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SkillLabel from '../Common/SkillLabel';
import { mediaVariantUrl, mediaSrcSet } from '../../utils/mediaUrl';

// Reference palette
const COLORS = {
  sectionBg: '#FAF8F5',
  cardBg: '#EDE6DF',
  border: '#E7DED5',
  heading: '#7A4B2A',
  label: '#6B3F22',
  icon: '#9A6845',
  imageBorder: 'transparent',
  imageBg: '#FFFFFF',
  shadow: 'rgba(122,75,42,0.08)',
  shadowHover: 'rgba(122,75,42,0.18)',
  subtitle: '#9A6845',
  scrollbar: '#D8C7B8',
  scrollbarHover: '#7A4B2A',
};

// Card dimensions — slightly wider, shorter than before
const CARD_W_DESKTOP = 162;
const CARD_H_DESKTOP = 178;
const CARD_W_MOBILE = 112;
const CARD_H_MOBILE = 138;
const IMG_SIZE_DESKTOP = 88;
const IMG_SIZE_MOBILE = 64;

/**
 * Circular service image that keeps the service icon visible as a placeholder
 * and fades the real image in once it has decoded. This prevents the "empty
 * circle" flash while images download and reserves space to avoid layout shift.
 */
const ServiceCircleImage = ({ src, alt, IconComponent }) => {
  const [loaded, setLoaded] = useState(false);
  const displaySrc = mediaVariantUrl(src, 'thumb');
  const srcSet = mediaSrcSet(src);

  return (
    <>
      {(!loaded || !src) && IconComponent && (
        <IconComponent
          sx={{ fontSize: { xs: 26, md: 34 }, color: COLORS.icon, position: 'absolute' }}
        />
      )}
      {src && (
        <Box
          component="img"
          src={displaySrc}
          srcSet={srcSet}
          sizes="88px"
          alt={alt}
          width={96}
          height={96}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(false)}
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            objectFit: 'cover',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.35s ease',
          }}
        />
      )}
    </>
  );
};

const ServicesSection = ({ services, popularServices, location }) => {
  const navigate = useNavigate();

  const iconMap = React.useMemo(() => {
    const map = {};
    services.forEach((s) => {
      if (s?.name && s?.icon) map[s.name.toLowerCase()] = s.icon;
    });
    return map;
  }, [services]);

  const displayList = React.useMemo(() => {
    if (Array.isArray(popularServices) && popularServices.length > 0) {
      return popularServices.map((ps) => ({
        name: ps.name,
        image: ps.image || null,
        icon: iconMap[ps.name?.toLowerCase()] || null,
      }));
    }
    return services.map((s) => ({ name: s.name, icon: s.icon, image: null }));
  }, [popularServices, services, iconMap]);

  return (
    <Box sx={{ pt: { xs: 1.5, md: 2.5 }, pb: { xs: 1, md: 1.5 }, bgcolor: COLORS.sectionBg }}>
      <Container maxWidth="lg">
        {/* Eyebrow label with flanking lines */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            mb: 1,
          }}
        >
          <Box sx={{ flex: 1, maxWidth: 120, height: '1px', bgcolor: COLORS.border }} />
          <Typography
            sx={{
              fontSize: { xs: '0.62rem', md: '0.7rem' },
              fontWeight: 600,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: COLORS.subtitle,
              whiteSpace: 'nowrap',
            }}
          >
            Most Popular Services
          </Typography>
          <Box sx={{ flex: 1, maxWidth: 120, height: '1px', bgcolor: COLORS.border }} />
        </Box>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: { xs: 3, md: 4 },
            color: COLORS.heading,
            textAlign: 'center',
            fontSize: { xs: '1.35rem', sm: '1.65rem', md: '2rem' },
            letterSpacing: '-0.01em',
          }}
        >
          Most Popular Services
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(3, 1fr)',
              sm: 'repeat(4, 1fr)',
              md: 'none',
            },
            gridAutoFlow: { md: 'column' },
            gridAutoColumns: { md: `${CARD_W_DESKTOP}px` },
            overflowX: { md: 'auto' },
            gap: { xs: 1.5, sm: 2, md: 2 },
            pb: { md: 1.5 },
            px: { xs: 0.5, md: 0 },
            '&::-webkit-scrollbar': { height: 5 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: COLORS.scrollbar,
              borderRadius: 3,
              '&:hover': { background: COLORS.scrollbarHover },
            },
          }}
        >
          {displayList.map((serviceItem, index) => {
            const IconComponent =
              iconMap[serviceItem.name?.toLowerCase()] || serviceItem.icon || null;
            const imgSrc = serviceItem.image;

            return (
              <Box
                key={serviceItem.name || index}
                onClick={() => {
                  const params = new URLSearchParams();
                  if ((location || '').trim()) params.set('location', location.trim());
                  if (serviceItem.name) params.set('service', serviceItem.name);
                  navigate(`/search?${params.toString()}`);
                }}
                sx={{
                  cursor: 'pointer',
                  transition: 'transform 0.22s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    '& .service-card': {
                      boxShadow: `0 6px 20px ${COLORS.shadowHover}`,
                    },
                  },
                }}
              >
                <Box
                  className="service-card"
                  sx={{
                    bgcolor: COLORS.cardBg,
                    borderRadius: '16px',
                    border: `1px solid ${COLORS.border}`,
                    boxShadow: `0 2px 12px ${COLORS.shadow}`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: { xs: 1, md: 1.25 },
                    pt: { xs: 1.25, md: 1.5 },
                    pb: { xs: 1.25, md: 1.5 },
                    width: { xs: CARD_W_MOBILE, md: CARD_W_DESKTOP },
                    height: { xs: CARD_H_MOBILE, md: CARD_H_DESKTOP },
                    mx: 'auto',
                    transition: 'box-shadow 0.22s ease',
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      width: { xs: IMG_SIZE_MOBILE, md: IMG_SIZE_DESKTOP },
                      height: { xs: IMG_SIZE_MOBILE, md: IMG_SIZE_DESKTOP },
                      borderRadius: '50%',
                      overflow: 'hidden',
                      bgcolor: COLORS.imageBg,
                      border: `2.5px solid ${COLORS.imageBorder}`,
                      boxShadow: `0 2px 12px rgba(0,0,0,0.12), 0 0 0 3px rgba(255,255,255,0.7)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'box-shadow 0.22s ease, transform 0.22s ease',
                      '.service-card:hover &': {
                        boxShadow: `0 4px 18px rgba(0,0,0,0.18), 0 0 0 3px rgba(255,255,255,0.85)`,
                        transform: 'scale(1.07)',
                      },
                    }}
                  >
                    <ServiceCircleImage
                      src={imgSrc}
                      alt={serviceItem.name}
                      IconComponent={IconComponent}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      fontWeight: 700,
                      color: COLORS.label,
                      textAlign: 'center',
                      fontSize: { xs: '0.84rem', md: '1rem' },
                      lineHeight: 1.3,
                      letterSpacing: '0.01em',
                      px: 0.5,
                    }}
                  >
                    <SkillLabel name={serviceItem.name} />
                  </Typography>

                  {IconComponent && (
                    <IconComponent
                      sx={{
                        fontSize: { xs: 20, md: 24 },
                        color: COLORS.icon,
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default React.memo(ServicesSection);
